import { streamChatSse } from "@/zapi-frontend/shared/api/chatSse";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import { ChatShell } from "@/zapi-frontend/shared/ui/chat";
import { LeoWelcome } from "@/zapi-frontend/widgets/LeoChat/ui/LeoWelcome";
import { useStagedAttachments } from "@/zapi-frontend/widgets/CreateProjectChat/ui/useStagedAttachments";
import { ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { CaseBriefChatMessage } from "@/zapi-frontend/widgets/CreateProjectChat/ui/CaseBriefChatMessage";
import { countVisibleAssistantMessages } from "@/zapi-frontend/shared/hooks/useChatUnreadIndicator";
import $api from "@/zapi-frontend/shared/api/axios";
import i18n, { projectLanguageToLng } from "@/zapi-frontend/shared/i18n";
import { translateCaseBriefStatus } from "@/zapi-frontend/shared/i18n/caseBriefStatus";

type SSEEvent = {
  type: "text" | "document_update" | "tool_start" | "tool_end" | "status" | "done" | "error";
  content?: string;
  delta?: string;
  tool?: string;
  message?: string;
  status?: string;
};

type Props = {
  projectId: number;
  language: string;
  onDocumentUpdate: (content: string) => void;
  onChatActivity?: () => void;
  onUploadFiles?: (files: File[]) => Promise<void>;
  onAddWebLink?: (url: string) => Promise<void>;
  onAssistantMessageCountChange?: (count: number) => void;
  readOnly?: boolean;
  appearance?: "default" | "leo";
};

function buildVaultSuccessMessage(language: string, hasFiles: boolean): ChatMessageType {
  const t = i18n.getFixedT(projectLanguageToLng(language), "createProject");
  return {
    id: uuidv4(),
    role: "assistant",
    content: JSON.stringify({ type: "answer", content: hasFiles ? t("vaultSuccess") : t("vaultLinkSuccess") })
  };
}

function buildIntroMessage(language: string): ChatMessageType {
  const t = i18n.getFixedT(projectLanguageToLng(language), "createProject");
  return {
    id: uuidv4(),
    role: "assistant",
    content: JSON.stringify({ type: "answer", content: t("refineIntro") })
  };
}

export function EditProjectChat({
  projectId,
  language,
  onDocumentUpdate,
  onChatActivity,
  onUploadFiles,
  onAddWebLink,
  onAssistantMessageCountChange,
  readOnly = false,
  appearance = "default"
}: Props) {
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingMsgId, setLoadingMsgId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const attachments = useStagedAttachments({ disabled: isGenerating });
  const chatHistoryRef = useRef<{ role: "user" | "assistant"; content: string }[]>([]);
  const chatSessionIdRef = useRef<string>(uuidv4());
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isLeo = appearance === "leo";
  const openingMessages = useCallback(() => (isLeo ? [] : [buildIntroMessage(language)]), [isLeo, language]);

  const persistMessages = useCallback(
    (next: ChatMessageType[]) => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        $api
          .patch(`/chat/edit-project-chat/messages/${projectId}`, {
            messages: next.map((m) => ({ id: m.id, role: m.role, content: m.content }))
          })
          .catch((err) => console.error("Failed to persist edit chat messages:", err));
      }, 500);
    },
    [projectId]
  );

  useEffect(() => {
    let cancelled = false;
    $api
      .get<{ messages: ChatMessageType[] }>(`/chat/edit-project-chat/messages/${projectId}`)
      .then((res) => {
        if (cancelled) return;
        const stored = Array.isArray(res.data.messages) ? res.data.messages : [];
        if (!stored.some((message) => message.role === "user")) {
          setMessages(openingMessages());
          chatHistoryRef.current = [];
        } else {
          setMessages(stored);
          chatHistoryRef.current = stored
            .filter((m) => m.role === "user" || m.role === "assistant")
            .map((m) => ({ role: m.role as "user" | "assistant", content: extractPlainContent(m) }));
        }
      })
      .catch((err) => {
        console.error("Failed to load edit chat history:", err);
        if (!cancelled) {
          setMessages(openingMessages());
          chatHistoryRef.current = [];
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, openingMessages]);

  useEffect(() => {
    if (isLoading) return;
    onAssistantMessageCountChange?.(countVisibleAssistantMessages(messages));
  }, [isLoading, messages, onAssistantMessageCountChange]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isGenerating]);

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, []);

  const t = useMemo(() => i18n.getFixedT(projectLanguageToLng(language), "createProject"), [language]);

  const handleSubmit = useCallback(
    async (messageText: string) => {
      if (!messageText.trim() || isGenerating) return;

      const userMsg: ChatMessageType = {
        id: uuidv4(),
        role: "user",
        content: messageText
      };
      const botMsgId = uuidv4();
      const botMsg: ChatMessageType = {
        id: botMsgId,
        role: "assistant",
        content: JSON.stringify({ type: "answer", content: "" })
      };

      const startingMessages = [...messages, userMsg, botMsg];
      setMessages(startingMessages);
      persistMessages(startingMessages);
      onChatActivity?.();

      setIsGenerating(true);
      setLoadingMsgId(botMsgId);
      setStatusMessage(translateCaseBriefStatus("Analyzing user message"));

      let accumulatedText = "";

      try {
        const apiBaseUrl = $api.defaults.baseURL || "";
        await streamChatSse<SSEEvent>({
          url: `${apiBaseUrl}/chat/edit-project-chat/generate`,
          body: {
            projectId,
            message: messageText,
            messagesHistory: chatHistoryRef.current,
            chatSessionId: chatSessionIdRef.current
          },
          onEvent: (event) => {
            switch (event.type) {
              case "text":
                accumulatedText += event.content || "";
                setLoadingMsgId(null);
                setStatusMessage(null);
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === botMsgId
                      ? { ...m, content: JSON.stringify({ type: "answer", content: accumulatedText }) }
                      : m
                  )
                );
                break;
              case "status":
                if (event.status) setStatusMessage(translateCaseBriefStatus(event.status));
                break;
              case "document_update":
                if (event.content) onDocumentUpdate(event.content);
                break;
              case "done":
                break;
            }
          }
        });

        const newHistory = [
          ...chatHistoryRef.current,
          { role: "user" as const, content: messageText },
          { role: "assistant" as const, content: accumulatedText }
        ];
        chatHistoryRef.current = newHistory;

        setMessages((prev) => {
          persistMessages(prev);
          return prev;
        });
      } catch (error) {
        console.error("Edit project chat generation failed:", error);
        toast.error(t("chatError"));
        setMessages((prev) =>
          prev.map((m) =>
            m.id === botMsgId
              ? {
                  ...m,
                  content: JSON.stringify({ type: "answer", content: t("chatErrorMessage") })
                }
              : m
          )
        );
      } finally {
        setIsGenerating(false);
        setLoadingMsgId(null);
        setStatusMessage(null);
      }
    },
    [isGenerating, messages, projectId, persistMessages, onDocumentUpdate, onChatActivity, t]
  );

  const handleAttachmentsSend = useCallback(
    async (files: File[], links: string[]) => {
      const items = [
        ...files.map((f) => ({ kind: "file" as const, label: f.name })),
        ...links.map((l) => ({ kind: "link" as const, label: l }))
      ];
      if (items.length === 0) return;

      const userMsg: ChatMessageType = {
        id: uuidv4(),
        role: "user",
        content: JSON.stringify({ type: "vault_items", items })
      };
      setMessages((prev) => {
        const next = [...prev, userMsg];
        persistMessages(next);
        return next;
      });
      onChatActivity?.();

      try {
        const uploadPromise = files.length > 0 && onUploadFiles ? onUploadFiles(files) : Promise.resolve();
        const linkPromises = links.map((url) => (onAddWebLink ? onAddWebLink(url) : undefined));
        await Promise.all([uploadPromise, ...linkPromises]);

        const successMsg = buildVaultSuccessMessage(language, files.length > 0);
        setMessages((prev) => {
          const next = [...prev, successMsg];
          persistMessages(next);
          return next;
        });
      } catch (error) {
        console.error("Failed to upload attachments:", error);
        toast.error("Failed to add attachment. Please try again.");
      }
    },
    [onUploadFiles, onAddWebLink, onChatActivity, persistMessages, language]
  );

  const handleClearChat = useCallback(() => {
    const next = openingMessages();
    chatHistoryRef.current = [];
    chatSessionIdRef.current = uuidv4();
    setMessages(next);
    persistMessages(next);
  }, [openingMessages, persistMessages]);

  const messagesToRender = useMemo(() => messages, [messages]);
  const showsWelcome = isLeo && messages.length === 0;

  const submit = async () => {
    const text = input.trim();
    const { files, links } = attachments.take();
    if (!text && files.length === 0 && links.length === 0) return;
    setInput("");
    if (files.length || links.length) {
      await handleAttachmentsSend(files, links);
    }
    if (text) {
      await handleSubmit(text);
    }
  };

  return (
    <ChatShell
      appearance={appearance}
      className="h-full w-full"
      bodyClassName={isLeo ? undefined : "px-0"}
      title={isLeo ? t("leo.title", { ns: "projectHome" }) : t("chatTitle")}
      placeholder={isLeo ? t("leo.placeholderAnything", { ns: "projectHome" }) : t("composerPlaceholder")}
      submitLabel={isLeo ? t("leo.send", { ns: "projectHome" }) : undefined}
      footnote={isLeo ? t("leo.disclaimer", { ns: "projectHome" }) : undefined}
      busyNotice={isLeo ? t("leo.busy", { ns: "projectHome" }) : undefined}
      input={input}
      onInputChange={setInput}
      onSubmit={() => void submit()}
      isBusy={isGenerating}
      canSubmit={!!input.trim() || attachments.hasAttachments}
      hideComposer={readOnly}
      isLoading={isLoading}
      loadingLabel={t("loadingMessages", { ns: "common" })}
      dockCloseLabel={isLeo ? t("leo.hide", { ns: "projectHome" }) : t("chat.hide", { ns: "projectHome" })}
      controlsLeft={attachments.attachButton}
      inputExtras={attachments.chips}
      scrollRef={scrollRef}
      onClear={readOnly ? undefined : handleClearChat}
      canClear={messages.some((message) => message.role === "user") && !isGenerating}
      inputTestId="edit-project-chat-input"
      sendTestId="edit-project-chat-send-button"
      clearTestId="edit-project-chat-clear-button"
    >
      {showsWelcome && (
        <LeoWelcome
          title={t("leo.welcomeTitle", { ns: "projectHome" })}
          subtitle={t("leo.frameWelcomeSubtitle", { ns: "projectHome" })}
          suggestions={[]}
          onPick={(suggestion) => void handleSubmit(suggestion)}
        />
      )}
      {messagesToRender.map((message) => (
        <CaseBriefChatMessage
          key={message.id}
          message={message}
          isLoading={isGenerating && message.id === loadingMsgId}
          progressMessage={message.id === loadingMsgId ? statusMessage : null}
        />
      ))}
      {attachments.overlays}
    </ChatShell>
  );
}

function extractPlainContent(m: ChatMessageType): string {
  if (m.role === "user") return m.content;
  try {
    const parsed = JSON.parse(m.content);
    if (parsed && typeof parsed === "object" && typeof parsed.content === "string") {
      return parsed.content;
    }
  } catch {
    // not JSON
  }
  return m.content;
}
